import { ConsumationType, FlightPaymentType, Role } from '@customTypes/types';
import { useShowFullClubProfileQuery } from '@features/work/api/show-full-club-profile';
import { ClubPhotoAccordion } from '@features/work/components/slider/ClubPhotoAccordion.tsx';
import { ClubVideoAccordion } from '@features/work/components/slider/ClubVideoAccordion.tsx';
import {
  ArrowsPointingOutIcon,
  MinusCircleIcon,
  TicketIcon,
  HomeModernIcon,
  ClockIcon,
  BanknotesIcon,
  HomeIcon,
  MapPinIcon,
  PhotoIcon,
  InformationCircleIcon
  // VideoCameraIcon
} from '@heroicons/react/24/outline';
import {
  Accordion,
  AccordionControl,
  Avatar,
  Box,
  Flex,
  Group,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useRatingsDueQuery } from '@shared/api/requests/artist/rating/get';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { ClubRating } from '@shared/utils/ClubRating.tsx';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// import { PhotoList } from '@/features/work/components/photo-list/PhotoList';
import { useClubConditionView } from '@/features/work/view/club-full-profile/hooks/useClubConditionView';
import { PageLoading } from '@/pages/PageLoading';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBModalFullscreenLayout } from '@/shared/layouts';
import {
  LBAccordionContainer,
  LBBadge,
  LBLinkButton,
  LBListItem,
  LBModalHeader
  // LBNavLink
} from '@/shared/ui-kit/components';

// import { VideoList } from '../../components/video-list/VideoList';

export const ClubFullProfile = ({ clubId }: { clubId: string }) => {
  const {
    state: {
      profileInfo,
      profilePhoto,
      profilePhotos,
      housingPhotos,
      isLoading,
      profileVideo
    }
  } = useClubConditionView({ clubId });

  const profile = useProfileStore((state) => state.profile);
  const isArtist = profile?.role === Role.artist;
  const artistId = isArtist ? profile?.profileIds[0] : null;

  const { data: dueItemsData } = useRatingsDueQuery(
    { artistProfileId: artistId?.toString() ?? '' },
    {
      options: {
        enabled: Boolean(isArtist && artistId) // hook always runs, but API call only when valid
      }
    }
  );

  const { refetch } = useShowFullClubProfileQuery({ profileId: clubId });

  useEffect(() => {
    refetch();
  }, []);

  const clubProfileId = Number(clubId);

  const [
    CrazyMenuModalStatus,
    { open: openCrazyMenuModal, close: closeCrazyMenuModal }
  ] = useDisclosure();
  const [
    ConsumationModalStatus,
    { open: openConsumationModal, close: closeConsumationModal }
  ] = useDisclosure();
  const { formatTime } = useIntl();

  const currentCurrency = profileInfo.data?.data.clubInfo?.currencySymbol;

  const daysOrder = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const sortedData = React.useMemo(
    () =>
      [...(profileInfo.data?.data.schedule ?? [])].sort(
        (a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day)
      ),
    [profileInfo.data?.data.schedule]
  );

  if (isLoading) return <PageLoading />;

  const openVacancies = [
    profileInfo.data?.data.clubInfo?.isHostessVacancyOpen && 'Hostess',
    profileInfo.data?.data.clubInfo?.isGoGoVacancyOpen && 'GoGo',
    profileInfo.data?.data.clubInfo?.isStripVacancyOpen && 'Strip'
  ]
    .filter(Boolean) // removes false/null values
    .join(', '); // create a single string like "Strip, GoGo"

  return (
    <>
      <LBModalFullscreenLayout
        opened={CrazyMenuModalStatus}
        onClose={closeCrazyMenuModal}
      >
        <LBModalHeader onClose={closeCrazyMenuModal} />
        <Title>{translate('commons.crazyMenu')}</Title>
        <Space h={20} />
        <Text fz={14}>
          {translate('pages.clubFullProfile.paymentFrequescyMessage')}{' '}
          <FormattedMessage
            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.crazyMenuPaymentFrequency}`}
          />
          .
        </Text>
        <Space h={20} />

        {!!profileInfo.data?.data.payments?.crazyMenu && (
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              width: '100%',
              border: '1px solid var(--base-light-main)',
              borderRadius: '8px'
            }}
          >
            {/* Заголовки */}
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                borderBottom: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.position')}
            </div>
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                borderBottom: '1px solid var(--base-light-main)',
                borderLeft: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.guest')}
            </div>
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                borderBottom: '1px solid var(--base-light-main)',
                borderLeft: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.roles.artist')}
            </div>

            {/* Данные */}
            {profileInfo.data?.data.payments?.crazyMenu.map((item, index) => (
              <React.Fragment key={item.name}>
                <div
                  style={{
                    gridColumn: '1 / -1',
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      padding: '10px 12px',
                      fontWeight: 500,
                      fontSize: '13px',
                      borderTop:
                        index === 0
                          ? 'none'
                          : '1px solid var(--base-light-main)'
                    }}
                  >
                    {item.name}
                  </div>
                  <div
                    style={{
                      padding: '10px 12px',
                      fontSize: '13px',
                      textAlign: 'center',
                      borderLeft: '1px solid var(--base-light-main)',
                      borderTop:
                        index === 0
                          ? 'none'
                          : '1px solid var(--base-light-main)'
                    }}
                  >
                    {item.price} {currentCurrency}
                  </div>
                  <div
                    style={{
                      padding: '10px 12px',
                      fontSize: '13px',
                      textAlign: 'center',
                      borderLeft: '1px solid var(--base-light-main)',
                      borderTop:
                        index === 0
                          ? 'none'
                          : '1px solid var(--base-light-main)'
                    }}
                  >
                    {profileInfo.data?.data.payments?.crazyMenuPercent
                      ? (item.price *
                          (profileInfo.data?.data.payments?.crazyMenuPercent ??
                            0)) /
                        100
                      : '-'}{' '}
                    {currentCurrency}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </Box>
        )}
      </LBModalFullscreenLayout>
      {/*  CrazyMenu */}
      {/* ConsumationMenu */}
      <LBModalFullscreenLayout
        opened={ConsumationModalStatus}
        onClose={closeConsumationModal}
      >
        <LBModalHeader onClose={closeConsumationModal} />

        <Title>{translate('commons.artistConsumation.label')}</Title>
        <Space h={20} />
        <Text fz={14}>
          {translate('pages.clubFullProfile.paymentFrequescyMessage')}{' '}
          <FormattedMessage
            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.consummationFrequency}`}
          />
          .
        </Text>
        <Space h={20} />
        {!!profileInfo.data?.data.payments?.consummationItems && (
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              width: '100%'
            }}
          >
            {/* Заголовки */}
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                borderRadius: '6px 0 0 0',
                border: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.position')}
            </div>
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                border: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.guest')}
            </div>
            <div
              style={{
                fontWeight: 600,
                backgroundColor: 'var(--base-light-main)',
                padding: '10px 12px',
                fontSize: '15px',
                borderRadius: '0 6px 0 0',
                border: '1px solid var(--base-light-main)'
              }}
            >
              {translate('commons.roles.artist')}
            </div>

            {/* Данные */}
            {profileInfo.data?.data.payments?.consummationItems.map((item) => (
              <div
                key={item.name}
                style={{
                  gridColumn: '1 / -1',
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  alignItems: 'center',
                  padding: '0',
                  // boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  borderRadius: '6px',
                  marginTop: '8px',
                  overflow: 'hidden'
                }}
              >
                {/* Название с переносом скобок */}
                <div
                  style={{
                    padding: '10px 12px',
                    fontWeight: 600,
                    fontSize: '13px',
                    borderRight: '1px solid var(--base-light-main)'
                  }}
                >
                  {item.name.replace(/\s*\((.*?)\)\.?$/, '').trim()}{' '}
                  {item.name.match(/\((.*?)\)\.?$/) && (
                    <div
                      style={{
                        fontWeight: 400,
                        fontSize: '12px',
                        color: 'var(--text-secondary, #666)',
                        marginTop: '2px'
                      }}
                    >
                      ({item.name.match(/\((.*?)\)\.?$/)?.[1]})
                    </div>
                  )}
                </div>
                <div
                  style={{
                    padding: '10px 12px',
                    fontSize: '13px',
                    borderRight: '1px solid var(--base-light-main)',
                    textAlign: 'center'
                  }}
                >
                  {item.price} {currentCurrency}
                </div>
                <div
                  style={{
                    padding: '10px 12px',
                    fontSize: '13px',
                    textAlign: 'center'
                  }}
                >
                  {item.reward} {currentCurrency}
                </div>
              </div>
            ))}
          </Box>
        )}
      </LBModalFullscreenLayout>
      {/*  ConsumationMenu */}

      <div>
        <Flex align='center' gap={16}>
          {!!profilePhoto.data?.data.length &&
          profilePhoto.data?.data[0].filePath ? (
            <Avatar
              size={110}
              src={resolveImagePath(profilePhoto.data?.data[0].filePath)}
            />
          ) : (
            <Avatar size={110}>
              {profileInfo.data?.data.clubInfo?.name.slice(0, 2)}
            </Avatar>
          )}
          <Flex align='start' direction='column' gap={6} w='100%'>
            <Title order={3}>{profileInfo.data?.data.clubInfo?.name}</Title>
            <Text fz={14} style={{ lineHeight: 1.8 }}>
              <MapPinIcon height={14} width={14} />{' '}
              {profileInfo.data?.data.clubInfo?.country} <br />
              {profileInfo.data?.data.clubInfo?.address}
            </Text>
            {artistId && (
              <ClubRating
                clubProfileId={clubProfileId}
                dueItems={dueItemsData?.data ?? []}
                mode='artist'
                ratingAverage={profileInfo.data?.data?.ratingAverage ?? null}
                averageRating={
                  profileInfo.data?.data?.ratingAverage?.totalAvg || 0
                }
              />
            )}
          </Flex>
        </Flex>
        <Space h={16} />
        {/* <Flex direction='column'> */}

        <Space h={16} />
        <Stack gap={16}>
          <LBAccordionContainer defaultValue='club_info'>
            <Accordion.Item value='club_info'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <InformationCircleIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.info')}
                  </Text>
                </Group>
              </AccordionControl>
              <Accordion.Panel>
                <Flex direction='column'>
                  <LBListItem>
                    <Flex align='center' justify='space-between' w='100%'>
                      <Text fw={400} fz={14}>
                        {translate('commons.vacancy')}
                      </Text>
                      <Text fw={600} fz={14}>
                        {openVacancies || translate('commons.noVacancies')}
                      </Text>
                    </Flex>
                  </LBListItem>
                  <LBListItem>
                    <Flex align='center' justify='space-between' w='100%'>
                      <Text fw={400} fz={14}>
                        {translate('commons.artistHiringIsOpen')}
                      </Text>
                      <Text fw={600} fz={14}>
                        {profileInfo.data?.data?.clubInfo?.isHiring ? (
                          <LBBadge c='white' color='green' variant='filled'>
                            {translate('commons.yes')}
                          </LBBadge> // if true → yes
                        ) : (
                          <LBBadge c='white' color='green' variant='filled'>
                            {translate('commons.no')}
                          </LBBadge>
                        )}
                      </Text>
                    </Flex>
                  </LBListItem>
                  <Space h={6} />
                  <LBListItem>
                    <Flex align='center' justify='space-between' w='100%'>
                      <Text fw={400} fz={14}>
                        {translate('commons.acceptWithExperience')}
                      </Text>
                      <Text fw={600} fz={14}>
                        {
                          profileInfo.data?.data?.workingConditions
                            ?.experienceRequired ? (
                            // ?   <Text fw={600}>
                            //   {translate('commons.no')}
                            // </Text>  // if true → yes
                            // : <Text fw={600}>
                            //   {translate('commons.yes')}
                            // </Text>
                            <LBBadge c='white' color='green' variant='filled'>
                              {translate('commons.no')}
                            </LBBadge> // if true → no
                          ) : (
                            <LBBadge c='white' color='green' variant='filled'>
                              {translate('commons.yes')}
                            </LBBadge>
                          ) // if false → YES
                        }
                      </Text>
                    </Flex>
                  </LBListItem>

                  <LBListItem>
                    <Text fw={300} fz={14}>
                      {translate(
                        'pages.clubFullProfile.minimalContractDuration'
                      )}
                    </Text>
                    <Text fw={700} fz={14}>
                      {' '}
                      {
                        profileInfo.data?.data?.contractTerms
                          ?.minContractDurationWeeks
                      }
                    </Text>
                    {/* <Text fw={700} fz={14}>&nbsp;{translate('commons.contractWeeks', { */}
                    {/*  count: */}
                    {/*    profileInfo.data?.data.contractTerms */}
                    {/*      ?.minContractDurationWeeks || 0 */}
                    {/* })}</Text> */}
                  </LBListItem>
                  <LBListItem>
                    <Text fw={300} fz={14}>
                      {translate('pages.clubFullProfile.artistWorkSchedule')}
                      {' '}
                    </Text>
                    <Text fw={700} fz={14}>
                      &nbsp;
                      {profileInfo.data?.data.contractTerms?.artistWorkSchedule}
                    </Text>
                  </LBListItem>
                  <Space h={8} />
                </Flex>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='payments'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <BanknotesIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.payments')}
                  </Text>
                </Group>
              </AccordionControl>
              <Accordion.Panel>
                {!!profileInfo.data?.data.payments?.clubFee && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.clubFee')}</Text>
                    {profileInfo.data?.data.payments?.clubFee ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.clubFee}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments?.clubFreeFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.clubFreeFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.dailyRateMin && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.rate')}</Text>
                    {profileInfo.data?.data.payments?.dailyRateMin ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.dailyRateMin}
                        {currentCurrency}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.guarantee && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.guarantee')}</Text>
                    {profileInfo.data?.data.payments?.guarantee ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.guarantee}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.guaranteePaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.guaranteePaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.deposit && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.deposit')}</Text>
                    {profileInfo.data?.data.payments?.deposit ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.deposit}
                        {currentCurrency}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.show && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.show')}</Text>
                    {profileInfo.data?.data.payments?.show ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.show}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.showPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.showPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.tips && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.tea')}</Text>
                    {profileInfo.data?.data.payments?.tips ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.tips} %
                        {/* /{' '}
                        {profileInfo.data?.data.payments
                          .tipsPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments.tipsPaymentFrequency}`}
                          />
                        )} */}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.consummationItems
                  .length && (
                  <LBListItem>
                    <Text fz={14}>
                      {translate('commons.artistConsumation.label')}
                    </Text>
                    <LBLinkButton onClick={() => openConsumationModal()}>
                      {translate('commons.open')}
                    </LBLinkButton>
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.crazyMenu.length && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.crazyMenu')}</Text>
                    <LBLinkButton onClick={() => openCrazyMenuModal()}>
                      {translate('commons.open')}
                    </LBLinkButton>
                  </LBListItem>
                )}
                {(!!profileInfo.data?.data.payments?.topless ||
                  !!profileInfo.data?.data.payments?.full ||
                  !!profileInfo.data?.data.payments?.goGo) && (
                  <Text fz={14} mt={10}>
                    {translate('commons.separated')}:
                  </Text>
                )}

                {!!profileInfo.data?.data.payments?.topless && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.topless')}</Text>
                    {profileInfo.data?.data.payments?.topless ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.topless}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.toplessPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.toplessPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.full && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.full')}</Text>
                    {profileInfo.data?.data.payments?.full ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.full}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.fullPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.fullPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.goGo && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.artistJobs.GoGo')}</Text>
                    {profileInfo.data?.data.payments?.goGo ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.goGo}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.goGoPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.goGoPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='photos'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <PhotoIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('pages.clubFullProfile.clubPhoto')}
                  </Text>
                </Group>
              </AccordionControl>
              <ClubPhotoAccordion
                photos={profilePhotos?.data?.data.map((p) => p.filePath) ?? []}
              />
            </Accordion.Item>

            <Accordion.Item value='home'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <HomeIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('pages.clubFullProfile.clubHousingPhoto')}
                  </Text>
                </Group>
              </AccordionControl>
              <ClubPhotoAccordion
                photos={housingPhotos?.data?.data.map((p) => p.filePath) ?? []}
              />
            </Accordion.Item>

            <Accordion.Item value='videos'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <PhotoIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('pages.clubFullProfile.clubVideo')}
                  </Text>
                </Group>
              </AccordionControl>
              <ClubVideoAccordion videos={profileVideo?.data?.data ?? []} />
            </Accordion.Item>

            <Accordion.Item value='work-hours'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <ClockIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.workingHours')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.workingHours')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                {sortedData.map((item) => (
                  <LBListItem key={item.day}>
                    <Text fz={14}>
                      <FormattedMessage id={`commons.schedule.${item.day}`} />
                    </Text>
                    {item.isClosed ? (
                      <Text fz={14}>{translate('commons.closed')}</Text>
                    ) : item.isOpen24Hours ? (
                      <Text fz={14}>{translate('commons.aroundTheClock')}</Text>
                    ) : (
                      <Text fz={12}>
                        {formatTime(
                          new Date().setHours(
                            +item.startHour.slice(0, 2),
                            +item.startHour.slice(3, 5)
                          )
                        )}{' '}
                        -{' '}
                        {formatTime(
                          new Date().setHours(
                            +item.closeHour.slice(0, 2),
                            +item.closeHour.slice(3, 5)
                          )
                        )}
                      </Text>
                    )}
                  </LBListItem>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='housing'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <HomeModernIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.housing')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.housing')}</Title> */}
                {/* {profileInfo.data?.data.housing?.isHousingPaidByClub ? ( */}
                {/*  <Text fz={14}>{translate('commons.isPaydByClub')}</Text> */}
                {/* ) : ( */}
                {/*  <Text fz={11}>{translate('commons.isntPaydByClub')}</Text> */}
                {/* )} */}
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>{translate('commons.rentPayment')}</Text>
                  {profileInfo.data?.data.housing?.isHousingPaidByClub ? (
                    <Text fz={11} ta='center'>
                      {translate('commons.isPaydByClub')}
                    </Text>
                  ) : (
                    <Text fz={14}>
                      {profileInfo.data?.data.housing?.housingPrice}{' '}
                      {currentCurrency} /
                      {profileInfo.data?.data.housing
                        ?.housingPaymentFrequency && (
                        <FormattedMessage
                          id={`commons.paymentFrequency.${
                            profileInfo.data?.data.housing
                              ?.housingPaymentFrequency
                          }`}
                        />
                      )}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.utilityPayments')}</Text>
                  {profileInfo.data?.data.housing?.isUtilityFeePaidByClub ? (
                    <Text fz={11}>{translate('commons.isPaydByClub')}</Text>
                  ) : (
                    <Text fz={14}>
                      {profileInfo.data?.data.housing?.utilityFee}{' '}
                      {currentCurrency} /{' '}
                      {profileInfo.data?.data.housing
                        ?.utilityPaymentFrequency && (
                        <FormattedMessage
                          id={`commons.paymentFrequency.${
                            profileInfo.data?.data.housing
                              ?.utilityPaymentFrequency
                          }`}
                        />
                      )}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.peopleInRoom')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data.housing?.peoplePerRoom}
                  </Text>
                </LBListItem>
                <Space h={8} />
                {profileInfo.data?.data.housing?.isClubHousingMandatory && (
                  <Text fw={700} fz={12}>
                    {translate('commons.housigInClubApartsRequired')}
                  </Text>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='ticket'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <TicketIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.flightTickets')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.flightTickets')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.transportFromAirport.label')}
                  </Text>
                  {profileInfo.data?.data.contractTerms
                    ?.isTransferOrganizedByClub ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <Space h={8} />
                <LBListItem>
                  <Text fz={14}>{translate('commons.payment')}</Text>
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Agreement && (
                    <Text fz={14}>
                      {' '}
                      {translate('commons.flightPaymentType.Agreement')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Artis && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Artist')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Club && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Club')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Credit && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Credit')}
                    </Text>
                  )}
                </LBListItem>

                {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                  FlightPaymentType.Compensation && (
                  <Stack gap={8}>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.oneMonth'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForOneMonth
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {' '}
                        {translate(
                          'commons.flightCompensationOptions.twoMonths'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForTwoMonths
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.threeMonths'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForThreeMonths
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.sixMonths'
                        )}
                      </Text>
                      {profileInfo.data?.data.contractTerms
                        ?.freeReturnTicketAfterSixMonths ? (
                        <Text fz={14}>
                          {translate(
                            'commons.flightCompensationOptions.backTicket'
                          )}
                        </Text>
                      ) : (
                        <Text fz={14}>{translate('commons.no')}</Text>
                      )}
                    </LBListItem>
                  </Stack>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='dress-code'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <img alt='dress' height={20} src='/dress.svg' width='20' />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.dressCode')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.dressCode')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                {profileInfo.data?.data?.workingConditions?.dressCodes.map(
                  (item) => (
                    <LBListItem key={item}>
                      <Text fz={14}>{item}</Text>
                    </LBListItem>
                  )
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='penalty'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <MinusCircleIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.penalties')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.penalties')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                {profileInfo.data?.data.penalties?.map((item) => (
                  <LBListItem key={item.penaltyItemId}>
                    <Text fz={14}>{item.name}</Text>
                    <Text fz={14}>
                      {item.fine} {currentCurrency}
                    </Text>
                  </LBListItem>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='other'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <ArrowsPointingOutIcon width={17} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.other')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.other')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>{translate('commons.teching')}</Text>
                  {profileInfo.data?.data?.workingConditions?.artistTraining ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.choreographerInClub')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.choreographerPresence ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.artistConsumation.label')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.consummationType === ConsumationType.Active ? (
                    <Text fz={14}>
                      {translate('commons.artistConsumation.Active')}
                    </Text>
                  ) : (
                    <Text fz={14}>
                      {translate('commons.artistConsumation.Passive')}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.photoshootParticipation')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.photoshootMandatory ? (
                    <Text fz={14}>{translate('commons.required')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.optional')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.artistsInShift')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data?.workingConditions?.artistsPerShift}
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.scenes')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data?.workingConditions?.numberOfStages}
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.dynamicPole')}</Text>
                  <Text fz={14}>
                    {
                      profileInfo.data?.data?.workingConditions
                        ?.numberOfPolesDynamic
                    }
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.staticPole')}</Text>
                  <Text fz={14}>
                    {
                      profileInfo.data?.data?.workingConditions
                        ?.numberOfPolesStatic
                    }
                  </Text>
                </LBListItem>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='about'>
              <AccordionControl>
                <Group align='center' gap='xs' p={2}>
                  <img alt='club' height={20} src='/club.svg' width={20} />
                  <Text c='#4f5053' fw={300} fz={14}>
                    {translate('commons.aboutClub')}
                  </Text>
                </Group>
                {/* <Title order={4}>{translate('commons.aboutClub')}</Title> */}
              </AccordionControl>
              <Accordion.Panel>
                <Text fz={14} style={{ whiteSpace: 'pre-wrap' }}>
                  {
                    profileInfo.data?.data?.workingConditions
                      ?.additionalInformation
                  }
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </LBAccordionContainer>
        </Stack>
      </div>
    </>
  );
};
